<template>
  <!--
    This img tag is not shown, but needed to fetch drag thumbnail efficiently in Mimir Desktop.
    This also causes an extra fetch of the image since it uses the same URL but with a question
    mark (?) or an ampersand (&) appended. Reason for this is the same as described in setPosterImage
    of clients/vuetify/src/model/video.ts.
  -->
  <!-- FIXME: Wrong events -->
  <!-- @vue-ignore -->
  <img
    ref="hiddenThumbnailImageTag"
    crossorigin="anonymous"
    :src="unCachedThumbnailUrl"
    style="display: none"
    @load="dragThumbnailLoaded"
    @error="thumbnailLoadFailed"
  >
</template>

<script lang="ts">
import type { LoadedItem } from '@mimir/common/dto/searchResultHit';
import { getDataUrl } from '@mimir/web/util/misc';
import { getThumbUrl } from '@mimir/web/util/thumbnail';
import type { PropType } from '@mimir/web/compat/vueCompat';
import { defineComponent } from '@mimir/web/compat/vueCompat';
import { isInElectron, type ApplicationContext } from '@mimir/common/context/applicationContext';
import { invariant } from '@mimir/common/util/invariant';

export default defineComponent({
  name: 'DesktopDragThumbnail',
  props: {
    applicationContext: {
      type: Object as PropType<ApplicationContext>,
      required: true,
    },
    item: {
      type: Object as PropType<LoadedItem | null>,
      default: null,
    },
    thumbnailDataUrl: {
      type: String as PropType<string | undefined>,
      default: undefined,
    },
  },
  data() {
    return {
      imageLoadFailed: false,
    };
  },
  mounted() {
    invariant(isInElectron(this.applicationContext), 'Should not use DesktopDragThumbnail outside of Electron context');
  },
  computed: {
    unCachedThumbnailUrl(): string {
      const url = this.thumbnailUrl;
      return `${url}${url.includes('?') ? '&' : '?'}`;
    },
    thumbnailUrl(): string {
      if (!this.item) {
        return '';
      }

      if (this.item.resolvedThumbnailUrl) {
        return this.item.resolvedThumbnailUrl;
      }

      if (!this.imageLoadFailed && this.item.thumbnail) {
        return getThumbUrl(this.item.thumbnail, this.item.itemType, this.item.originalFileName);
      }

      return getThumbUrl('', this.item.itemType, this.item.originalFileName);
    },
  },
  watch: {
    'item.thumbnail'() {
      this.imageLoadFailed = false;
    },
  },
  methods: {
    inElectron() {
      return isInElectron(this.applicationContext);
    },
    thumbnailLoadFailed(failedSrc: string) {
      this.imageLoadFailed = true;
    },
    dragThumbnailLoaded(loadedSrc: string) {
      if (!this.inElectron() || !this.item) {
        return;
      }
      const image = this.$refs.hiddenThumbnailImageTag as HTMLImageElement;
      const dataUrl = getDataUrl(image);
      if (dataUrl) {
        this.$emit('update:thumbnailDataUrl', dataUrl);
      }
    },
  },
});
</script>
